import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { PrimaryButton } from '../../components/common/Buttons';
import OfflineInfoBannerIcon from '../../images/consultations/new-flow/SelfBookInfoIcon.svg';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: flex-end;
`;

const BottomSheet = styled.div`
  width: 100%;
  background: #ffffff;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  position: relative;
  height: auto;
  max-height: 80%;
  max-width: 440px;
  display: flex;
  flex-direction: column;
  overflow: visible;
`;

const ContentContainer = styled.div`
  padding: 28px;
  padding-bottom: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  min-height: 0;
  width: 100%;
  margin-top: -100px;
  box-sizing: border-box;
  z-index: 1;
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  z-index: 2;
  pointer-events: none;
`;

const HeaderIcon = styled.img`
  margin-top: -50px;
  margin-bottom: 16px;
`;

const HeaderText = styled.div`
  font-family: Inter;
  font-weight: 700;
  font-style: italic;
  font-size: 24px;
  line-height: 140%;
  text-align: center;
  color: #000000;
  margin-bottom: 4px;
`;

const SubHeader = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-style: italic;
  font-size: 14px;
  line-height: 140%;
  text-align: center;
  color: #e79000;
  margin-bottom: 8px;
  white-space: pre-line;
`;

const StepList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  margin-bottom: 24px;
`;

const StepItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const StepNumber = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-family: Inter;
  line-height: 140%;
  flex-shrink: 0;

  ${(props) =>
    props.active
      ? `
    background: linear-gradient(#ffbb00, #ffbb00) padding-box,
                linear-gradient(151.06deg, #242189 15.67%, #DAE4FF 50.33%, #72B4E2 87.14%) border-box;
    border: 3px solid transparent;            
    color: #FFFFFF;
    font-weight: 800;
    font-size: 13px;
  `
      : `
    color: #41395899;
    font-weight: 500;
    font-size: 13px;
  `}
`;

const StepDescription = styled.div`
  font-family: Inter;
  line-height: 140%;
  font-size: 13px;
  text-align: left;

  ${(props) =>
    props.active
      ? `
    font-weight: 700;
    color: #000000;
  `
      : `
    font-weight: 500;
    color: #41395899;
  `}
`;

const CarouselWrapper = styled.div`
  width: 100%;
  position: relative;
  flex-shrink: 0;
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const SlideContainer = styled.div`
  width: 100%;
  flex: 0 0 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  scroll-snap-align: center;
`;

const SlideImage = styled.img`
  width: 100%;
  max-width: 312px;
  height: 162px;
  border-radius: 12px;
  object-fit: cover;
`;

const PaginationDotsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  margin-top: 10px;
  width: 100%;
  max-width: 311px;
`;

const Dot = styled.div`
  transition: all 0.3s ease;
  cursor: pointer;
  ${(props) =>
    props.active
      ? `
    width: 12px;
    height: 4px;
    border-radius: 12px;
    background: #ffbb00;
    box-shadow: -8.75px 8.75px 8.75px 0px #FFFFFF1A inset, 8.75px -8.75px 8.75px 0px #BDBBC21A inset;
    backdrop-filter: blur(10.5px);
  `
      : `
    width: 4px;
    height: 4px;
    border-radius: 12px;
    background: #41395866;
    box-shadow: -8.75px 8.75px 8.75px 0px #FFFFFF1A inset, 8.75px -8.75px 8.75px 0px #BDBBC21A inset;
    backdrop-filter: blur(10.5px);
  `}
`;

const FooterContainer = styled.div`
  width: 100%;
  padding: 12px 28px;
  background: #ffffff;
  flex-shrink: 0;
  z-index: 10;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

const SkipButton = styled.button`
  background: none;
  border: none;
  color: #8852cc;
  font-size: 14px;
  padding: 10px;
  cursor: pointer;
  font-weight: 500;
  width: 100%;

  ${({ hidden }) =>
    hidden &&
    `
      visibility: hidden;
      pointer-events: none;
    `}
`;

const PreBookInfoModal = ({ onClose, onComplete, data }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState([]);
  const carouselRef = useRef(null);
  useEffect(() => {
    if (data && data.descriptionTexts) {
      setSteps(data.descriptionTexts);
    }
  }, [data]);

  const handleScroll = () => {
    if (carouselRef.current) {
      const scrollLeft = carouselRef.current.scrollLeft;
      const width = carouselRef.current.offsetWidth;
      const newStep = Math.round(scrollLeft / width);
      if (newStep !== currentStep) {
        setCurrentStep(newStep);
      }
    }
  };

  const scrollToStep = (index) => {
    if (carouselRef.current) {
      const width = carouselRef.current.offsetWidth;
      carouselRef.current.scrollTo({
        left: width * index,
        behavior: 'smooth',
      });
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      scrollToStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  if (!steps || steps.length === 0) {
    return (
      <ModalOverlay onClick={onClose}>
        <BottomSheet onClick={(e) => e.stopPropagation()}>
          <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>
        </BottomSheet>
      </ModalOverlay>
    );
  }

  return (
    <ModalOverlay onClick={onClose}>
      <BottomSheet onClick={(e) => e.stopPropagation()}>
        <IconContainer>
          <HeaderIcon src={OfflineInfoBannerIcon} alt="Info" />
        </IconContainer>

        <ContentContainer>
          <HeaderText>{data?.title || 'You Book, We Pay!'}</HeaderText>
          <SubHeader>
            {data?.title2 || (
              <>
                Unlike other consults,
                <br />
                you must book this yourself.
              </>
            )}
          </SubHeader>
          <StepList>
            {steps.map((step, index) => (
              <StepItem key={index} onClick={() => scrollToStep(index)}>
                <StepNumber active={index === currentStep}>
                  {index + 1}
                </StepNumber>
                <StepDescription active={index === currentStep}>
                  {step.title}
                </StepDescription>
              </StepItem>
            ))}
          </StepList>

          <CarouselWrapper ref={carouselRef} onScroll={handleScroll}>
            {steps.map((step, index) => (
              <SlideContainer key={index}>
                <SlideImage
                  src={step.icon}
                  alt={`prebook-${index}-info-image`}
                />
              </SlideContainer>
            ))}
          </CarouselWrapper>

          <PaginationDotsContainer>
            {steps.map((_, index) => (
              <Dot
                key={index}
                active={index === currentStep}
                onClick={() => scrollToStep(index)}
              />
            ))}
          </PaginationDotsContainer>
        </ContentContainer>

        <FooterContainer>
          <PrimaryButton onTap={handleNext}>
            <div className="text-body-l font-bold text-center py-4px">
              {currentStep === steps.length - 1 ? 'Got it!' : 'Next'}
            </div>
          </PrimaryButton>
          <SkipButton
            hidden={currentStep === steps.length - 1}
            onClick={onComplete}
          >
            Skip
          </SkipButton>
        </FooterContainer>
      </BottomSheet>
    </ModalOverlay>
  );
};

export default PreBookInfoModal;
